import React from "react";
import { Grid, Tooltip } from "@mui/material";
import { Banknote, CheckCircle2, MoreVertical, PenLine } from "lucide-react";
import { Button as ShadButton } from "@/components/ui/button";
import { Badge as ShadBadge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DebitPermissionDto,
  GetRechnungDetailsDto,
  PatientBillInfoDto,
} from "../../../../api-client";
import StandardPaymentButtons from "./StandardPaymentButtons";
import SepaBillActions from "../sepa/SepaBillActions";
import PerLastschriftButton from "../sepa/PerLastschriftButton";
import { isPaymentBlocked } from "../sepa/sepaBill";
import { useActiveRcurMandates, usePendingOoffMandate } from "../hooks/useActiveRcurMandates";
import { MandateStatusEnum } from "../../../../stores/Sepa/sepa.types";
import useStore from "../../../../helpers/useStore";
import { PatientsContext } from "../../../../stores/Patients/patients.provider";

interface PatientBillActionsProps {
  bill: PatientBillInfoDto;
  rechnungen?: GetRechnungDetailsDto[];
  rxPrice: number;
  validSepaPermissions: DebitPermissionDto[];
  sepaMenuState: {
    anchorEl: HTMLElement | null;
    openMenus: { [key: string]: boolean };
  };
  isRowBusy: (rxId?: string) => boolean;
  onClickPay: (bill: PatientBillInfoDto) => void;
  onClickBill: (bill: PatientBillInfoDto) => void;
  onClickPaybackToPatient: (bill: PatientBillInfoDto) => void;
  /** Opens the one-off wizard directly to resume an in-progress OOFF mandate. */
  onResumeEinmal?: (bill: PatientBillInfoDto) => void;
  onHandleSepaButtonClick: (
    bill: PatientBillInfoDto,
    validSepaPermissions: DebitPermissionDto[],
  ) => void;
  onHandleSepaSelect: (bill: PatientBillInfoDto, permission: DebitPermissionDto) => void;
  onCloseSepaMenu: (rxId?: string) => void;
  handleExternalBill: (bill: PatientBillInfoDto) => void;
}

const PatientBillActions: React.FC<PatientBillActionsProps> = ({
  bill,
  rechnungen,
  rxPrice,
  validSepaPermissions,
  sepaMenuState,
  isRowBusy,
  onClickPay,
  onClickBill,
  onResumeEinmal,
  onHandleSepaButtonClick,
  onHandleSepaSelect,
  onCloseSepaMenu,
  handleExternalBill,
}) => {
  // Patient id sourced from the store so the new SEPA actions are self-sufficient
  // without threading a new prop through the table/row.
  const patientId = useStore(PatientsContext).selectedPatientId as number | undefined;

  // ACTIVE recurring (RCUR) mandates for this bill's creditor account. When at least
  // one exists we lead with the "per Lastschrift" button and tuck the standard payment
  // actions (Zahlen / Rechnung erstellen) into a kebab menu.
  const activeRcurMandates = useActiveRcurMandates(patientId, bill.accountId);
  const hasActiveMandate = activeRcurMandates.length > 0;

  // An in-progress one-off (OOFF) mandate for this Rx (DRAFT = signature pending,
  // ACTIVE = signed but not yet collected) — surfaced as a primary "resume" action.
  const pendingOoff = usePendingOoffMandate(patientId, bill.rxId);

  // Calculate total amount from all rechnungen
  const totalRechnungAmount =
    rechnungen?.reduce((total, rechnung) => total + rechnung.amount, 0) || 0;

  // Blocked first: a blocked bill is provisional too, and "Zahlung nicht möglich"
  // is the more actionable of the two messages. The blocking rule itself is
  // unchanged; only the estimate wording now follows isProvisionalPrice, which
  // also covers slots that exist but have never been scheduled.
  const tooltipTitle =
    bill.isEstimated && !bill.isTimeSlotZero
      ? "Zahlung nicht möglich"
      : bill.isProvisionalPrice
        ? "Dies ist ein geschätzter Preis."
        : rechnungen && totalRechnungAmount.toFixed(2) === rxPrice.toFixed(2)
          ? ""
          : "Klicken zum Bezahlen";

  let actionElement: React.ReactNode;

  // Kebab of standard actions, shared by the "per Lastschrift" and one-off-resume layouts.
  const moreMenu = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <ShadButton
          size="sm"
          aria-label="Weitere Zahlungsoptionen"
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-0 text-slate-600 shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.6)] transition-all duration-200 hover:from-slate-50 hover:to-slate-100 hover:text-slate-900 active:scale-[0.97] [&_svg]:size-4"
        >
          <MoreVertical />
        </ShadButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onClickPay(bill)}>Zahlen</DropdownMenuItem>
        <DropdownMenuItem disabled={isRowBusy(bill.rxId)} onClick={() => onClickBill(bill)}>
          Rechnung erstellen
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  /**
   * Shared payable actions block. Used for any bill that still has an amount due
   * (with or without existing rechnungen).
   *
   * When an ACTIVE recurring mandate exists for this bill's account, we lead with the
   * prominent "per Lastschrift" button and move the standard actions (Zahlen / Rechnung
   * erstellen) into a kebab menu — direct debit is the primary path in that case.
   * Otherwise we keep the standard buttons inline.
   */
  const payableActions = (
    <Grid container gap={1} display="flex" justifyContent="flex-end" alignItems="center">
      {pendingOoff && patientId != null && onResumeEinmal ? (
        <>
          <ShadButton
            size="sm"
            disabled={isPaymentBlocked(bill)}
            onClick={() => onResumeEinmal(bill)}
            className="flex h-7 items-center gap-1.5 rounded-lg border-0 bg-gradient-to-b from-[#4189a8] to-[#367b9a] px-3 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:from-[#3b7f9b] hover:to-[#2f6b86] active:scale-[0.97] disabled:opacity-50 [&_svg]:size-4"
          >
            {pendingOoff.status === MandateStatusEnum.Active ? (
              <>
                <Banknote />
                Einziehen
              </>
            ) : (
              <>
                <PenLine />
                Fortsetzen
              </>
            )}
          </ShadButton>
          {moreMenu}
        </>
      ) : hasActiveMandate && patientId != null ? (
        <>
          <PerLastschriftButton
            bill={bill}
            patientId={patientId}
            mandates={activeRcurMandates}
            disabled={isPaymentBlocked(bill)}
          />
          {moreMenu}
        </>
      ) : (
        <StandardPaymentButtons
          bill={bill}
          isRowBusy={isRowBusy}
          onClickPay={onClickPay}
          onClickBill={onClickBill}
          handleExternalBill={handleExternalBill}
        />
      )}
    </Grid>
  );

  // Handle overpaid cases
  if (bill.dueAmount < 0) {
    return (
      <div className="flex justify-end gap-1">
        {patientId != null && <SepaBillActions bill={bill} patientId={patientId} />}
      </div>
    );
  }

  // Handle fully paid cases
  if (!rechnungen && bill.dueAmount === 0) {
    return (
      <div className="flex justify-end">
        <ShadBadge
          variant="outline"
          className="gap-1 border-emerald-200 bg-emerald-200 px-2.5 py-1 text-sm font-medium text-emerald-800"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          Bezahlt
        </ShadBadge>
      </div>
    );
  }

  // No rechnungen created yet
  if (!rechnungen || rechnungen.length === 0) {
    actionElement = payableActions;
  } else {
    if (rechnungen && bill.dueAmount === 0) {
      return (
        <div className="flex justify-end">
          <ShadBadge
            variant="outline"
            className="gap-1 border-emerald-200 bg-emerald-200 px-2.5 py-1 text-sm font-medium text-emerald-800"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {rechnungen.length} Rechnung{rechnungen.length > 1 ? "en" : ""} erstellt
          </ShadBadge>
        </div>
      );
    } else {
      /**
       * Partially paid case: a rechnung exists but there is still an amount due.
       * Show the standard payment buttons plus the "per Lastschrift" SEPA button
       * (when a valid debit mandate exists) so the remaining Fällig can be
       * charged via direct debit or any standard method.
       */
      actionElement = payableActions;
    }
  }

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <span>{actionElement}</span>
    </Tooltip>
  );
};

export default PatientBillActions;
